import { useEffect, useReducer } from 'react';

import { calculateRoi } from '../calculations';
import { CURRENCY_CONFIGS, DEFAULT_INPUTS, STORAGE_KEY } from '../constants';
import styles from '../style.module.scss';
import { type AppAction, type AppState, type StoredReport } from '../types';
import CalculatorInput from './CalculatorInput';
import EmailCapture from './EmailCapture';
import Report from './Report';

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_INPUT':
      return {
        ...state,
        inputs: { ...state.inputs, [action.field]: action.value },
      };
    case 'SET_CURRENCY':
      return { ...state, currency: action.currency };
    case 'CALCULATE': {
      // Skip email capture if user already submitted previously
      const nextPhase = state.hasSubmitted ? 'report' : 'email-capture';
      if (state.hasSubmitted) {
        const report: StoredReport = {
          inputs: state.inputs,
          currency: state.currency,
          hasSubmitted: true,
          timestamp: Date.now(),
        };
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(report));
        } catch {
          // localStorage unavailable
        }
      }
      return { ...state, phase: nextPhase, calcCount: state.calcCount + 1 };
    }
    case 'SUBMIT_SUCCESS': {
      const report: StoredReport = {
        inputs: state.inputs,
        currency: state.currency,
        hasSubmitted: true,
        timestamp: Date.now(),
      };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(report));
      } catch {
        // localStorage unavailable
      }
      return { ...state, phase: 'report', hasSubmitted: true };
    }
    case 'RESTORE':
      return { ...state, ...action.state };
    default:
      return state;
  }
}

const INITIAL_STATE: AppState = {
  phase: 'input',
  inputs: { ...DEFAULT_INPUTS },
  currency: 'usd',
  hasSubmitted: false,
  calcCount: 0,
};

interface RoiCalculatorAppProps {
  heroTitle: string;
  heroDescription: string;
  hubspotEmbed: string;
  pricingPageUrl: string;
}

export default function RoiCalculatorApp({
  heroTitle,
  heroDescription,
  hubspotEmbed,
  pricingPageUrl,
}: RoiCalculatorAppProps) {
  const [state, dispatch] = useReducer(appReducer, INITIAL_STATE);

  // Restore from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;
      const report: StoredReport = JSON.parse(stored);
      if (report.inputs && report.currency) {
        dispatch({
          type: 'RESTORE',
          state: {
            inputs: report.inputs,
            currency: report.currency,
            hasSubmitted: report.hasSubmitted ?? false,
            phase: 'report',
          },
        });
      }
    } catch {
      // Invalid stored data
    }
  }, []);

  // Scroll to report on every calculate (calcCount changes each time)
  useEffect(() => {
    if (state.phase === 'input' || state.calcCount === 0) return;
    const target = document.getElementById('report-start');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.calcCount]);

  const currencyConfig = CURRENCY_CONFIGS[state.currency];
  const results = calculateRoi(state.inputs, currencyConfig);

  return (
    <div className={styles.roiCalculator}>
      {/* Hero section — always visible (hidden in print via CSS) */}
      <section className={styles.heroSection}>
        <div className={styles.heroInner}>
          <div>
            <h1
              className={styles.heroTitle}
              dangerouslySetInnerHTML={{
                __html: heroTitle.replace(/\bROI\b/g, '<em>ROI</em>'),
              }}
            />
            <p className={styles.heroDescription}>{heroDescription}</p>
          </div>
          <CalculatorInput inputs={state.inputs} currency={state.currency} dispatch={dispatch} />
        </div>
      </section>

      {/* Email capture phase */}
      {state.phase === 'email-capture' && (
        <EmailCapture
          results={results}
          currency={state.currency}
          hubspotEmbed={hubspotEmbed}
          pricingPageUrl={pricingPageUrl}
          dispatch={dispatch}
        />
      )}

      {/* Full report phase */}
      {state.phase === 'report' && (
        <Report
          results={results}
          inputs={state.inputs}
          currency={state.currency}
          pricingPageUrl={pricingPageUrl}
        />
      )}
    </div>
  );
}
